/**
 * @file        TeamPage.jsx
 * @module      modules/kyb/manager/console
 * @description Team and SLA — who has what, and what's at risk of missing
 *              the one-working-day target (shared HTML design). A KYB manager
 *              sees their own team here, and "My team" to add and manage
 *              team members.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { kybManagerApi } from "../api/kybManagerApi";
import { initials } from "./ui";
import MyTeam from "./MyTeam";

export default function TeamPage({ me }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const [reload, setReload] = useState(0);
  useEffect(() => {
    kybManagerApi
      .team()
      .then(setData)
      .catch((e) => setError(e.message || "Couldn't load the team."));
  }, [reload]);
  const myTeam = me?.canManageTeam ? <MyTeam onChanged={() => setReload((n) => n + 1)} /> : null;

  const head = (
    <div className="pagehead">
      <div>
        <h1>Team and SLA</h1>
        <p>Who has what, and what's at risk of missing the one-working-day target.</p>
      </div>
    </div>
  );
  if (error) return (
    <>
      {head}
      <div className="empty">{error}</div>
      {myTeam}
    </>
  );
  if (!data) return (
    <>
      {head}
      <div className="loading">Loading…</div>
    </>
  );

  const max = Math.max(1, ...data.stats.map((s) => s.open + s.awaiting));
  const { unassigned } = data;
  return (
    <>
      {head}
      {unassigned.length ? (
        <div className="banner warn" style={{ margin: "0 0 16px" }}>
          <div>
            <b>
              {unassigned.length} case{unassigned.length > 1 ? "s" : ""} not assigned
            </b>
            {unassigned.map((c) => c.name).join(", ")}. Assign them so the SLA has an owner.
          </div>
          <button className="btn small ghost" onClick={() => navigate("/kyb-manager?tab=review&assignee=none")}>
            Show unassigned
          </button>
        </div>
      ) : null}
      <div className="tablewrap">
        <table className="q" style={{ minWidth: 760 }}>
          <thead>
            <tr>
              <th>Reviewer</th>
              <th>Workload</th>
              <th>Needs review</th>
              <th>Awaiting Customer</th>
              <th>Due in 4 hours</th>
              <th>Overdue</th>
              <th>Closed</th>
            </tr>
          </thead>
          <tbody>
            {data.stats.map((s) => (
              <tr key={s.id} onClick={() => navigate(`/kyb-manager?tab=all&assignee=${s.id}`)}>
                <td>
                  <span style={{ display: "inline-flex", gap: 10, alignItems: "center" }}>
                    <span className={`avatar ${s.id === me?.id ? "" : "grey"}`}>{initials(s.name)}</span>
                    <b>{s.name}</b>
                  </span>
                </td>
                <td>
                  <div className="bar">
                    <b style={{ width: `${((s.open + s.awaiting) / max) * 100}%` }} />
                  </div>
                </td>
                <td>{s.open}</td>
                <td>{s.awaiting}</td>
                <td style={{ color: s.soon ? "var(--warn)" : "inherit", fontWeight: s.soon ? 700 : 400 }}>{s.soon}</td>
                <td style={{ color: s.overdue ? "var(--bad)" : "inherit", fontWeight: s.overdue ? 700 : 400 }}>{s.overdue}</td>
                <td>{s.closed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ color: "var(--muted)", fontSize: 12.5, marginTop: 10 }}>
        SLA: one working day from submission for cases needing review. The clock pauses while waiting on the Customer and restarts when they respond.
      </p>
      {myTeam}
    </>
  );
}
